import { useEffect, useState } from "react";
import { AuthService } from "../services/auth.service";

const API_BASE_URL = "/api";

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  monthlyPrice: string;
  yearlyPrice: string | null;
  orderLimit: number | null;
}

export interface ActiveSubscription {
  id: string;
  status: "TRIAL" | "ACTIVE" | "PAST_DUE" | "CANCELLED" | "EXPIRED";
  billingCycle: string;
  currentPeriodEnd: string;
  trialEndsAt: string | null;
  // Orders charged to the plan in the current period. Already present in the
  // /billing/subscription payload (raw passthrough of the subscription row).
  usageCount: number;
  plan: SubscriptionPlan;
}

interface BillingState {
  subscription: ActiveSubscription | null;
  pricingUrl: string | null;
  loading: boolean;
  error: string | null;
}

async function apiFetch(path: string): Promise<Response> {
  const token = await AuthService.getShopifyToken();
  if (!token) throw new Error("No Shopify token available");
  return fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function useBilling(): BillingState {
  const [state, setState] = useState<BillingState>({
    subscription: null,
    pricingUrl: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // After plan approval Shopify redirects back with ?charge_id=... in the URL.
        // In that case sync directly with Shopify to avoid waiting for the webhook.
        // On normal visits just read from the DB.
        const hasChargeId = new URLSearchParams(window.location.search).has(
          "charge_id"
        );
        const subscriptionPath = hasChargeId
          ? "/billing/sync"
          : "/billing/subscription";

        const [subRes, urlRes] = await Promise.all([
          apiFetch(subscriptionPath),
          apiFetch("/billing/pricing-url"),
        ]);

        // Only the subscription is critical for rendering the plan. The pricing
        // URL just powers a "change plan" link, so a failure there (e.g. a 503
        // when SHOPIFY_APP_HANDLE is unset) must NOT blank out the whole plan
        // section — degrade to a null pricingUrl instead.
        if (!subRes.ok)
          throw new Error(`Subscription fetch failed: ${subRes.status}`);

        const subData = await subRes.json();
        const urlData = urlRes.ok ? await urlRes.json() : null;

        if (!cancelled) {
          setState({
            subscription: subData.subscription ?? null,
            pricingUrl: urlData?.pricingUrl ?? null,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            subscription: null,
            pricingUrl: null,
            loading: false,
            error: err instanceof Error ? err.message : "Unknown error",
          });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
